import { debounce } from './utils';
import { Component } from './component';
import { bindDrawerPanelState, teleportDrawerPanels } from './slide-in-portal';

let drawerComponent = null;
if (!customElements.get('drawer-component')) {
  class DrawerComponent extends Component {
    /** @type {HTMLElement | null} */
    #panelContent = null;

    /** @type {HTMLElement | null} */
    #panelBackdrop = null;

    /** @type {(() => void) | null} */
    #unbindPanelState = null;

    connectedCallback() {
      super.connectedCallback();

      if (!this.details) return;

      const { content, backdrop } = teleportDrawerPanels(this, { replaceExisting: true });
      this.#panelContent = content;
      this.#panelBackdrop = backdrop;
      this.#unbindPanelState = bindDrawerPanelState(this.details, content, backdrop);

      this._openedOnce = false;
      this.debouncedOnCloseButtonClicked = debounce(this.onCloseButtonClicked.bind(this), 0);
      if (this.closeButton) this.closeButton.addEventListener('click', this.debouncedOnCloseButtonClicked);
      if (this.backdrop) this.backdrop.addEventListener('click', this.debouncedOnCloseButtonClicked);
      this.onSummaryClickHandler = this.onSummaryClick.bind(this);
      this.onSummaryKeydownHandler = this.onSummaryKeydown.bind(this);
      if (this.summary) this.summary.addEventListener('click', this.onSummaryClickHandler, { capture: true });
      if (this.summary) this.summary.addEventListener('keydown', this.onSummaryKeydownHandler, { capture: true });
      this.details.addEventListener('toggle', this.onToggle.bind(this));
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (!this.details) return;

      this.#unbindPanelState?.();
      this.#unbindPanelState = null;
      if (this.summary && this.onSummaryClickHandler) this.summary.removeEventListener('click', this.onSummaryClickHandler, { capture: true });
      if (this.summary && this.onSummaryKeydownHandler) this.summary.removeEventListener('keydown', this.onSummaryKeydownHandler, { capture: true });
    }

    get details() {
      return this.querySelector('details');
    }
    get summary() {
      return this.details?.querySelector('summary');
    }
    get closeButton() {
      return this.#panelContent?.querySelector('button[type="button"][data-close-button]') ?? this.details?.querySelector('button[type="button"][data-close-button]');
    }

    get backdrop() {
      return this.#panelBackdrop ?? this.querySelector('.drawer__backdrop');
    }

    onCloseButtonClicked() {
      this.details?.removeAttribute('open');
    }

    onSummaryClick(event) {
      if (this._openedOnce) return;
      if (this.details.open) return;

      event.preventDefault();

      const panel = this.querySelector('.drawer');
      const backdrop = this.backdrop;

      this.details.open = true;
      if (panel) {
        panel.style.transition = 'none';
        panel.style.transform = 'translateX(100%)';
      }
      if (backdrop) {
        backdrop.style.transition = 'none';
        backdrop.style.opacity = '0';
        backdrop.style.pointerEvents = 'none';
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (panel) {
            panel.style.transition = '';
            panel.style.transform = '';
          }
          if (backdrop) {
            backdrop.style.transition = '';
            backdrop.style.opacity = '';
            backdrop.style.pointerEvents = '';
          }
          this._openedOnce = true;
        });
      });
    }

    onSummaryKeydown(event) {
      if (this._openedOnce) return;
      if (this.details.open) return;
      const key = event.key;
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.onSummaryClick(event);
      }
    }

    onToggle() {
      const { open } = this.details;

      const section = this.details.closest('.section-product-information');
      if (open) {
        if (section) section.classList.add('z-layer-window-overlay');
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        if (section) section.classList.remove('z-layer-window-overlay');
        document.documentElement.removeAttribute('scroll-lock');
      }
    }

    /**
     * Re-teleport drawer panels after section morphing injects fresh markup into the host.
     */
    reconcilePanels() {
      if (!this.details) return;

      this.#unbindPanelState?.();
      this.#unbindPanelState = null;

      const { content, backdrop } = teleportDrawerPanels(this, { replaceExisting: true });
      this.#panelContent = content;
      this.#panelBackdrop = backdrop;
      this.#unbindPanelState = bindDrawerPanelState(this.details, content, backdrop);

      if (this.closeButton && this.debouncedOnCloseButtonClicked) {
        this.closeButton.addEventListener('click', this.debouncedOnCloseButtonClicked);
      }

      if (this.backdrop && this.debouncedOnCloseButtonClicked) {
        this.backdrop.addEventListener('click', this.debouncedOnCloseButtonClicked);
      }
    }
  }
  customElements.define('drawer-component', DrawerComponent);
  drawerComponent = DrawerComponent;
}
export { drawerComponent };
